import React, { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { ModalBody, ModalFooter } from "application/modal/index";
import {
  peopleMembersGet,
  peopleMembersEdit,
} from "modules/people/internalPeople/internalPeople.action";
import {
  companySearchClear,
  searchInCouncil,
} from "modules/companies/companyMain/companyMain.action";
import ErrorBoundary from "common/components/errorBoundary";

import { useModelPopup } from "../../../../common/hooks";
import styles from "./editMember.module.scss";
import {
  Button,
  BtnType,
  TextBox,
  Label,
  DropSelect,
  MultiSelect,
  Loading,
} from "../../../primitives";
import { initModel, validation, permission } from "./editMember.constants";
import { userTeamsGet } from "../../../../common/actions/discussions.action";
import { peopleListTeamsGet } from "../../../../common/actions/common.action";
import { editCompanyContactsAttempt } from "../adminMembers.action";

const EditMemberComponent = (props) => {
  const history = useHistory();
  const {
    location: {
      state: { user },
    },
  } = history;
  const {
    councilReducer: { selectedCouncil },
    adminMembersReducer: {
      memberData,
      loading,
      shouldRedirectToMainFromContacts,
    },
    peopleReducer: {
      internalPeopleReducer: { teamList, selectedMember, shouldRedirectToMain },
    },
    companiesReducer: {
      companyMainReducer: { searchResults },
    },
  } = useSelector((state) => state);
  const fromContacts = history?.location?.state?.fromContacts;
  const popup = useModelPopup();
  const [model, setModel] = useState(initModel);
  const [companyOptions, setCompanyOptionst] = useState([]);
  const [selected, setSelected] = useState([]);
  const [keepOpenDropdown, setKeepOpenDropdown] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [elasticQueryy, setElasticQuery] = useState("");
  const [userConfirm, setUserConfirm] = useState("");
  const patientOrg = history.location.pathname.indexOf("organizations") !== -1;
  const formRef = useRef();
  const containerRef = useRef();
  const dropRef = useRef();

  useEffect(() => {
    if (searchResults?.length) {
      setCompanyOptionst([...searchResults]);
    }
  }, [searchResults]);

  const handleConfirm = () => {
    setKeepOpenDropdown(true);
    setUserConfirm("");
    popup.show({
      title: "Confirm",
      show: true,
      height: "300",
      width: "540",
      component: (
        <div>
          <ModalBody>
            <div>
              You are about to switch this member to a Role with more access to
              all the data on your Traction platform. Are you sure?
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                popup.hide();
                setUserConfirm(false);
                setKeepOpenDropdown(false);
              }}
              btn={BtnType.FRAME_LESS}
            >
              No
            </Button>
            <Button
              onClick={() => {
                setUserConfirm(true);
                setKeepOpenDropdown(false);

                popup.hide();
              }}
            >
              Yes
            </Button>
          </ModalFooter>
        </div>
      ),
    });
  };

  const handleTypeSearch = (val) => {
    setElasticQuery(val.search);
  };

  const handleOptionSelect = (values) => {
    setSelected(values?.obj || []);
  };

  useEffect(() => {
    if (selectedCouncil?.id) {
      dispatch(
        searchInCouncil({
          data: elasticQueryy,
          council_id: selectedCouncil?.id,
          mode:
            patientOrg && patientOrg
              ? "gsk/patient_organizations"
              : "companies",
          enqueueSnackbar,
          cb: () => {},
        })
      );
    }
  }, [elasticQueryy, selectedCouncil]);

  const handleOnSubmit = (values) => {
    const { first_name, last_name, title, email, council_role, team_ids } =
      values;
    const data = {
      first_name,
      last_name,
      title,
      email,
      council_role: council_role || "standard",
      enqueueSnackbar,
      team_ids,
      userId: history.location.state.user.user_id,
      memberId: history.location.state.user.user_id,
      cb: () => {
        if (fromContacts) {
          history.push("/admin/members/invited");
        } else {
          history.push("/admin/members");
        }
      },
    };
    if (selectedMember?.council_role === "company") {
      data.company_id = selected[0].id;
    }
    try {
      if (fromContacts) {
        dispatch(
          editCompanyContactsAttempt({
            enqueueSnackbar,
            data: {
              first_name,
              last_name,
              title,
              email,
              access_role: council_role || "company",
            },
            contact_id: history?.location?.state?.contactId,
          })
        );
      } else {
        dispatch(peopleMembersEdit(data));
      }
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    if (memberData) {
      setModel(memberData);
    }
  }, [memberData]);

  useEffect(() => {
    if (!fromContacts) {
      dispatch(
        peopleMembersGet({ memberId: props.match.params.id, enqueueSnackbar })
      );
    }
    dispatch(userTeamsGet({ enqueueSnackbar }));
  }, []);

  useEffect(() => {
    if (selectedCouncil) {
      dispatch(
        peopleListTeamsGet({
          councilId: selectedCouncil.id,
          enqueueSnackbar,
        })
      );
    }
  }, [selectedCouncil]);

  useEffect(() => {
    if (selectedMember) {
      const {
        first_name,
        last_name,
        title,
        email,
        council_role,
        teams,
        invited_company,
      } = selectedMember;

      if (invited_company) {
        setSelected([invited_company]);
      }

      const member = {
        first_name,
        last_name,
        title,
        email,
        council_role,
        team_ids: teams.length && teams.map((team) => team.id),
      };
      setModel(member);
    }
  }, [selectedMember]);

  useEffect(() => {
    if (shouldRedirectToMain || shouldRedirectToMainFromContacts) {
      if (history) {
        if (fromContacts && memberData.access_role === "company") {
          history.push("/admin/members/invited");
        } else {
          history.push("/admin/members");
        }
      }
    }
  }, [shouldRedirectToMain, shouldRedirectToMainFromContacts]);

  useEffect(() => {
    if (user) {
      setModel({
        ...user,
        council_role: user.access_role || user.council_role,
      });
    }
  }, [user]);

  useEffect(
    () => () => {
      dispatch(companySearchClear());
    },
    []
  );
  const current_role = localStorage.getItem("council_role");
  if (loading) {
    return <Loading />;
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <div className="row">
        <div className="col-10" style={{ margin: "0 auto" }}>
          <div className={styles.headerWrp}>
            <h4 className={styles.taskHeaderText}>
              {current_role &&
                (current_role === "ttp_administrator" ||
                  current_role === "council_administrator") && (
                  <span>
                    <span
                      className={styles.rootText}
                      onClick={() => {
                        history.push("/admin");
                      }}
                    >
                      ADMIN
                    </span>
                    <span className={styles.separationBar}> / </span>
                    <span
                      className={styles.rootText}
                      onClick={() => {
                        history.push("/admin/members");
                      }}
                    >
                      MEMBERS
                    </span>
                    <span className={styles.separationBar}> / </span>
                  </span>
                )}
              <span className={styles.companyName}>
                {selectedMember?.full_name}
              </span>
            </h4>
          </div>
          <ErrorBoundary>
            <Formik
              innerRef={formRef}
              enableReinitialize
              initialValues={model && model}
              validationSchema={validation && validation}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                setSubmitting(true);
                handleOnSubmit(values);
                resetForm();
              }}
            >
              {({ handleSubmit, ...formikprops }) => (
                <form onSubmit={handleSubmit}>
                  <div className={styles.editStageFormWrp}>
                    <Label>Email</Label>
                    <TextBox
                      type="text"
                      name="email"
                      placeholder="Email"
                      className={styles.textBox}
                      formProps={formikprops}
                    />
                    <div className={styles.name}>
                      <div className={styles.firstName}>
                        <Label>First Name</Label>
                        <TextBox
                          type="text"
                          name="first_name"
                          placeholder="First name"
                          className={styles.textBox}
                          formProps={formikprops}
                        />
                      </div>
                      <div className={styles.lastName}>
                        <Label>Last Name</Label>
                        <TextBox
                          type="text"
                          name="last_name"
                          placeholder="Last name"
                          className={styles.textBox}
                          formProps={formikprops}
                        />
                      </div>
                    </div>
                    <Label>Title</Label>
                    <TextBox
                      type="text"
                      name="title"
                      placeholder="Title"
                      className={styles.textBox}
                      formProps={formikprops}
                    />

                    <Label>Access Level</Label>
                    <DropSelect
                      placeholder="Choose a access level"
                      name="council_role"
                      labelField="text"
                      valueField="val"
                      searchBy="text"
                      handleConfirm={fromContacts && handleConfirm}
                      containerRef={containerRef}
                      dropRef={dropRef}
                      userConfirm={userConfirm}
                      keepOpenDropdown={keepOpenDropdown}
                      values={[
                        permission.find(
                          (item) => formikprops.values.council_role === item.val
                        ),
                      ]}
                      options={permission}
                      formProps={formikprops}
                    />
                    {!fromContacts && (
                      <div className={styles.lastName}>
                        <br />
                        <Label>Team</Label>
                        <MultiSelect
                          placeholder="Choose a team"
                          name="team_ids"
                          labelField="name"
                          valueField="id"
                          searchBy="name"
                          options={teamList || []}
                          formProps={formikprops}
                        />
                      </div>
                    )}
                    {selectedMember?.council_role === "company" && (
                      <>
                        <Label>Company</Label>
                        <DropSelect
                          // dropRef={multiselectRef}
                          onChange={handleOptionSelect}
                          placeholder="Select Company"
                          labelField="name"
                          valueField="id"
                          searchBy="name"
                          options={
                            companyOptions
                              ? companyOptions.filter((el) => el)
                              : []
                          }
                          values={selected}
                          multi={false}
                          searchFn={(args) => {
                            handleTypeSearch(args.state);
                          }}
                        />
                      </>
                    )}
                  </div>
                  <div className={styles.buttonWrp}>
                    <Button
                      type="submit"
                      btn={BtnType.REGULAR}
                      className={styles.add}
                      onClick={handleSubmit}
                    >
                      Save
                    </Button>
                  </div>
                </form>
              )}
            </Formik>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

export default EditMemberComponent;
